import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, ListGroup, Badge } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { FaShoppingCart, FaArrowLeft, FaCreditCard, FaMoneyBillWave, FaUniversity } from 'react-icons/fa';
import Layout from '../components/layout/Layout';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

const PAYSTACK_PUBLIC_KEY = 'pk_live_b4b05d77938ed75aa4c5f8348f21bb28f342c0fb'; // Replace with your public key

const Checkout = () => {
  const { cartItems, getTotalPrice, clearCart } = useCart();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('paystack');
  const [orderId, setOrderId] = useState(null);
  
  // Form states
  const [formData, setFormData] = useState({
    fullName: currentUser?.name || '',
    email: currentUser?.email || '',
    phoneNumber: currentUser?.phoneNumber || '',
    location: currentUser?.location || '',
    additionalInfo: ''
  });

  useEffect(() => {
    if (currentUser) {
      setFormData(prev => ({
        ...prev,
        fullName: currentUser.name || '',
        email: currentUser.email || '',
        phoneNumber: currentUser.phoneNumber || '',
        location: currentUser.location || ''
      }));
    }
  }, [currentUser]);

  // Generate a unique reference for Paystack
  const generateReference = () => `UT${Date.now()}${Math.floor(Math.random() * 10000)}`;

  // Prepare order items for backend
  const getOrderItems = () => cartItems.map(item => ({
    product: item.id,
    name: item.name,
    price: item.price,
    quantity: item.quantity,
    image: item.image || '',
    seller_name: item.sellerName || '',
    category: item.category || ''
  }));

  // Create order in backend
  const createOrder = async (reference) => {
    setLoading(true);
    setError('');
    try {
      const response = await api.post(
        '/orders/',
        {
          total: getTotalPrice(),
          delivery_address: formData.location,
          paystack_reference: reference,
          items: getOrderItems(),
        }
      );
      setOrderId(response.data.id);
      return response.data.id; // Return the order ID
    } catch (err) {
      setError('Failed to create order. Please try again.');
      setLoading(false);
      return null;
    }
  };

  // On form submit, create order and then show Paystack
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (cartItems.length === 0) {
      setError('Your cart is empty');
      return;
    }
    const reference = generateReference();
    const orderId = await createOrder(reference);
    if (orderId) {
      try {
        const paystackInitRes = await api.post('/orders/paystack-init/', {
          amount: getTotalPrice(),
          email: formData.email,
          order_id: orderId,
        });
        const { authorization_url } = paystackInitRes.data;
        window.location.href = authorization_url;
      } catch (err) {
        setError('Failed to initialize Paystack payment.');
      }
    }
  };
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  if (cartItems.length === 0 && !success) {
    return (
      <Layout>
        <Container className="py-5">
          <Card className="text-center p-5 shadow-sm">
            <Card.Body>
              <h3>Your cart is empty</h3>
              <p className="text-muted">
                You need to add products to your cart before checkout.
              </p>
              <Button 
                as={Link} 
                to="/products" 
                variant="primary"
                size="lg"
                className="mt-3"
              >
                <FaArrowLeft className="me-2" />
                Browse Products
              </Button>
            </Card.Body>
          </Card>
        </Container>
      </Layout>
    );
  }
  
  return (
    <Layout>
      <Container className="py-4">
        <h2 className="mb-4">Checkout</h2>
        
        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}
        
        <Form onSubmit={handleSubmit}>
          <Row>
            {/* Delivery Information */}
            <Col lg={8}>
              <Card className="shadow-sm mb-4">
                <Card.Header className="bg-white">
                  <h4 className="mb-0">Delivery Information</h4>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Full Name</Form.Label>
                        <Form.Control
                          type="text"
                          name="fullName"
                          value={formData.fullName}
                          onChange={handleInputChange}
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Email</Form.Label>
                        <Form.Control
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Phone Number</Form.Label>
                        <Form.Control
                          type="tel"
                          name="phoneNumber"
                          value={formData.phoneNumber}
                          onChange={handleInputChange}
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Location on Campus</Form.Label>
                        <Form.Control
                          type="text"
                          name="location"
                          value={formData.location}
                          onChange={handleInputChange}
                          required
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  
                  <Form.Group className="mb-3">
                    <Form.Label>Additional Information</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      name="additionalInfo"
                      value={formData.additionalInfo}
                      onChange={handleInputChange}
                      placeholder="Any specific delivery instructions or meeting preferences..."
                    />
                  </Form.Group>
                </Card.Body>
              </Card>
              
              {/* Payment Method */}
              <Card className="shadow-sm mb-4">
                <Card.Header className="bg-white">
                  <h4 className="mb-0">Payment Method</h4>
                </Card.Header>
                <Card.Body>
                  <Form.Group className="mb-4">
                    <div className="d-flex flex-wrap gap-3">
                      <div className="payment-option">
                        <Form.Check
                          type="radio"
                          id="mobile-money"
                          name="paymentMethod"
                          value="mobile-money"
                          checked={paymentMethod === 'mobile-money'}
                          onChange={() => setPaymentMethod('mobile-money')}
                          label={
                            <div className="d-flex align-items-center">
                              <FaMoneyBillWave className="me-2 text-success" />
                              <span>Mobile Money</span>
                            </div>
                          }
                          className="mb-2"
                        />
                      </div>
                      
                      <div className="payment-option">
                        <Form.Check
                          type="radio"
                          id="cash"
                          name="paymentMethod"
                          value="cash"
                          checked={paymentMethod === 'cash'}
                          onChange={() => setPaymentMethod('cash')}
                          label={
                            <div className="d-flex align-items-center">
                              <FaCreditCard className="me-2 text-primary" />
                              <span>Cash on Delivery</span>
                            </div>
                          }
                          className="mb-2"
                        />
                      </div>
                      
                      <div className="payment-option">
                        <Form.Check
                          type="radio"
                          id="bank-transfer"
                          name="paymentMethod"
                          value="bank-transfer"
                          checked={paymentMethod === 'bank-transfer'}
                          onChange={() => setPaymentMethod('bank-transfer')}
                          label={
                            <div className="d-flex align-items-center">
                              <FaUniversity className="me-2 text-info" />
                              <span>Bank Transfer</span>
                            </div>
                          }
                          className="mb-2"
                        />
                      </div>
                    </div>
                  </Form.Group>
                  
                  {paymentMethod === 'mobile-money' && (
                    <div className="mobile-money-details">
                      <h5 className="mb-3">Mobile Money Details</h5>
                      
                      <Form.Group className="mb-3">
                        <Form.Label>Select Provider</Form.Label>
                        <Form.Select
                          name="provider"
                          // value={mobileMoneyData.provider}
                          // onChange={handleMobileMoneyChange}
                          required
                        >
                          <option value="mtn">MTN Mobile Money</option>
                          <option value="vodafone">Vodafone Cash</option>
                          <option value="airtel">AirtelTigo Money</option>
                        </Form.Select>
                      </Form.Group>
                      
                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Mobile Money Number</Form.Label>
                            <Form.Control
                              type="tel"
                              name="phoneNumber"
                              // value={mobileMoneyData.phoneNumber}
                              // onChange={handleMobileMoneyChange}
                              required
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Account Name</Form.Label>
                            <Form.Control
                              type="text"
                              name="name"
                              // value={mobileMoneyData.name}
                              // onChange={handleMobileMoneyChange}
                              required
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                      
                      <Alert variant="info">
                        <p className="mb-0">
                          <strong>Payment Instructions:</strong> After placing your order, you will receive a prompt on your mobile phone to authorize the payment. Please complete the authorization to confirm your order.
                        </p>
                      </Alert>
                    </div>
                  )}
                  
                  {paymentMethod === 'cash' && (
                    <Alert variant="info">
                      <p className="mb-0">
                        <strong>Cash on Delivery:</strong> You will pay in cash when you meet the seller to collect your items. Please ensure you have the exact amount as sellers may not have change.
                      </p>
                    </Alert>
                  )}
                  
                  {paymentMethod === 'bank-transfer' && (
                    <Alert variant="info">
                      <p className="mb-0">
                        <strong>Bank Transfer Instructions:</strong> After placing your order, you will receive the seller's bank details via email. Please make the transfer within 24 hours and send the transaction receipt to confirm your order.
                      </p>
                    </Alert>
                  )}
                </Card.Body>
              </Card>
            </Col>
            
            {/* Order Summary */}
            <Col lg={4}>
              <Card className="shadow-sm">
                <Card.Header className="bg-white">
                  <h4 className="mb-0">Order Summary</h4>
                </Card.Header>
                <Card.Body>
                  <ListGroup variant="flush">
                    {cartItems.map(item => (
                      <ListGroup.Item key={item.id} className="px-0">
                        <div className="d-flex justify-content-between">
                          <div>
                            <div className="fw-bold">{item.name}</div>
                            <div className="text-muted small">
                              Qty: {item.quantity} x GH₵ {Number(item.price).toFixed(2)}
                            </div>
                          </div>
                          <div className="text-end">
                            <div>GH₵ {(Number(item.price) * item.quantity).toFixed(2)}</div>
                            <Badge bg="success" className="mt-1">{item.status}</Badge>
                          </div>
                        </div>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                  
                  <hr />
                  
                  <div className="d-flex justify-content-between mb-2">
                    <span>Subtotal</span>
                    <span>GH₵ {getTotalPrice().toFixed(2)}</span>
                  </div>
                  
                  <div className="d-flex justify-content-between mb-2">
                    <span>Delivery Fee</span>
                    <span>GH₵ 0.00</span>
                  </div>
                  
                  <div className="d-flex justify-content-between fw-bold">
                    <span>Total</span>
                    <span>GH₵ {getTotalPrice().toFixed(2)}</span>
                  </div>
                </Card.Body>
                <Card.Footer className="bg-white">
                  <Button 
                    type="submit" 
                    variant="primary" 
                    className="w-100"
                    size="lg"
                    disabled={loading}
                  >
                    {loading ? 'Processing...' : 'Place Order'}
                  </Button>
                  
                  <div className="d-flex justify-content-between mt-3">
                    <Button 
                      as={Link} 
                      to="/cart" 
                      variant="outline-secondary"
                      className="w-100 me-2"
                    >
                      <FaArrowLeft className="me-1" /> Back to Cart
                    </Button>
                    <Button 
                      as={Link} 
                      to="/products" 
                      variant="outline-primary"
                      className="w-100"
                    >
                      <FaShoppingCart className="me-1" /> Add More Items
                    </Button>
                  </div>
                  
                  <div className="mt-3 text-center">
                    <small className="text-muted">
                      By placing your order, you agree to our <Link to="/terms">Terms of Service</Link> and <Link to="/privacy">Privacy Policy</Link>.
                    </small>
                  </div>
                </Card.Footer>
              </Card>
            </Col>
          </Row>
        </Form>
      </Container>
    </Layout>
  );
};

export default Checkout;
